<template>
  <div
    v-if="value"
    class="image-lightbox-overlay"
    @click.self="close"
  >
    <div class="image-lightbox-toolbar">
      <v-btn
        icon
        dark
        title="Zoom in"
        :disabled="scale >= maxScale"
        @click="zoomIn"
      >
        <v-icon>mdi-magnify-plus-outline</v-icon>
      </v-btn>
      <v-btn
        icon
        dark
        title="Zoom out"
        :disabled="scale <= minScale"
        @click="zoomOut"
      >
        <v-icon>mdi-magnify-minus-outline</v-icon>
      </v-btn>
      <v-btn
        icon
        dark
        title="Reset"
        :disabled="scale === 1 && tx === 0 && ty === 0"
        @click="reset"
      >
        <v-icon>mdi-restore</v-icon>
      </v-btn>
      <v-btn
        icon
        dark
        title="Close"
        @click="close"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
    <img
      :src="src"
      class="image-lightbox-img"
      :class="{ 'image-lightbox-img--dragging': dragging }"
      :style="imgStyle"
      draggable="false"
      @wheel.prevent="onWheel"
      @mousedown.prevent="onMouseDown"
      @click.stop
    >
  </div>
</template>

<script>
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const WHEEL_FACTOR = 500;
const BUTTON_STEP = 0.5;

function clamp(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

export default {
  name: 'ImageLightbox',
  props: {
    value: { type: Boolean, default: false },
    src: { type: String, default: '' },
  },
  data() {
    return {
      scale: 1,
      tx: 0,
      ty: 0,
      dragging: false,
      dragStart: null,
      minScale: MIN_SCALE,
      maxScale: MAX_SCALE,
    };
  },
  computed: {
    cursor() {
      if (this.dragging) return 'grabbing';
      if (this.scale > 1) return 'grab';
      return 'zoom-in';
    },
    imgStyle() {
      return {
        transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`,
        cursor: this.cursor,
      };
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.reset();
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);
      } else {
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
  },
  methods: {
    close() {
      this.$emit('input', false);
    },
    reset() {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.dragging = false;
      this.dragStart = null;
    },
    zoomIn() {
      this.scale = clamp(this.scale + BUTTON_STEP, MIN_SCALE, MAX_SCALE);
    },
    zoomOut() {
      const next = clamp(this.scale - BUTTON_STEP, MIN_SCALE, MAX_SCALE);
      this.scale = next;
      // When fully zoomed out, recenter the image so the next zoom-in starts clean.
      if (next === 1) {
        this.tx = 0;
        this.ty = 0;
      }
    },
    onWheel(e) {
      this.scale = clamp(this.scale - e.deltaY / WHEEL_FACTOR, MIN_SCALE, MAX_SCALE);
      if (this.scale === 1) {
        this.tx = 0;
        this.ty = 0;
      }
    },
    onMouseDown(e) {
      if (this.scale <= 1) return;
      this.dragging = true;
      this.dragStart = {
        x: e.clientX,
        y: e.clientY,
        txStart: this.tx,
        tyStart: this.ty,
      };
    },
    onMouseMove(e) {
      if (!this.dragging || !this.dragStart) return;
      this.tx = this.dragStart.txStart + (e.clientX - this.dragStart.x);
      this.ty = this.dragStart.tyStart + (e.clientY - this.dragStart.y);
    },
    onMouseUp() {
      this.dragging = false;
      this.dragStart = null;
    },
    onKeyDown(e) {
      if (e.key === 'Escape') this.close();
      else if (e.key === '+' || e.key === '=') this.zoomIn();
      else if (e.key === '-' || e.key === '_') this.zoomOut();
      else if (e.key === '0') this.reset();
    },
  },
};
</script>

<style scoped lang="scss">
.image-lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.image-lightbox-toolbar {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 4px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  padding: 4px;
  z-index: 1;
}

.image-lightbox-img {
  max-width: 95vw;
  max-height: 95vh;
  transform-origin: center center;
  transition: transform 80ms ease-out;
  will-change: transform;
}

.image-lightbox-img--dragging {
  transition: none;
}
</style>
